/**
 * /service-request/:id -- Customer service-request detail page.
 *
 * Orchestrates which UI "phase" to show based on the workflow state:
 *
 *   service_selected              -> <ProcessingScreen>   (AI analysing, no user action)
 *   docs_start                    -> <DocumentUpload>     (upload required docs)
 *   provider_assignment_start ...
 *   service_request_complete      -> <ProviderWorkspace>  (full workspace: chat, steps, AI)
 *
 * Also renders a WorkflowStepper header so the customer always knows where
 * they are in the overall journey.
 */
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Skeleton } from '@finmin/ui';
import { useMyServiceRequest, serviceRequestKeys, isServiceLocked } from '@/api/service-requests';
import { queryClient }                             from '@/api/query-client';
import { WorkflowStepper }   from '@/features/service-request/components/workflow-stepper';
import { ProcessingScreen }  from '@/features/service-request/components/processing-screen';
import { DocumentUpload }    from '@/features/service-request/components/document-upload';
import { ProviderWorkspace } from '@/features/service-request/components/provider-workspace';
import { PaymentGateOverlay } from '@/features/service-request/components/payment-modal';
import { AdvancePaymentCard } from '@/features/service-request/components/advance-payment';
import { ServiceClosurePanel } from '@/features/service-request/components/service-closure-panel';
import { ServiceRequestBills } from '@/features/billing/bills-list';
import {
  PROCESSING_STATES,
  UPLOAD_STATES,
  WORKSPACE_STATES,
} from '@/features/service-request/constants';
import { getServiceMeta } from '@/features/dashboard/constants';
import { usePanConstraint, usePanView } from '@/features/dashboard/pan-view-context';
import { PageHeader } from '@/features/service-request/components/page-header';
import { PALETTE_FOREST, PALETTE_CREAM } from '@/features/service-request/tokens';

export const Route = createFileRoute('/_protected/service-request/$id')({
  component: ServiceRequestPage,
});

// -- Page -------------------------------------------------------------------

function ServiceRequestPage() {
  const { id } = Route.useParams();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const {
    data:      request,
    isLoading,
    isError,
    error,
    refetch,
  } = useMyServiceRequest(id, {
    // Retry on 404 up to 3 times (1 s apart) -- the backend write may not be
    // committed by the time we navigate here right after creation.
    retry: (failureCount, err) => err.status === 404 && failureCount < 3,
    retryDelay: 1_000,
    // While in the processing state, poll every 15 s so the page
    // auto-advances when the AI finishes without a manual refresh.
    refetchInterval: (query) => {
      const state = query.state.data?.customer_workflow_state;
      return state === 'service_selected' ? 15_000 : false;
    },
    staleTime: 30_000,
  });

  // The request is filed under one PAN, so the switcher stays on it here.
  const requestPanId = request?.customer_pan_id ?? null;
  usePanConstraint(
    requestPanId ? { kind: 'locked', panId: requestPanId } : { kind: 'none' },
  );

  // Keep the rest of the app on the PAN the customer is looking at.
  const { selection, setSelection } = usePanView();
  React.useEffect(() => {
    if (requestPanId && selection !== requestPanId) setSelection(requestPanId);
    // Only realign when the request's PAN changes, not on every selection edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPanId]);

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(id) });
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }

  // -- Loading skeleton -------------------------------------------------------

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: PALETTE_CREAM }}>
        <PageHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          <Skeleton className="h-3 w-48 rounded" />
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  // -- Error state ------------------------------------------------------------

  if (isError || !request) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: PALETTE_CREAM }}>
        <PageHeader />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center space-y-4">
          <div
            className="inline-flex h-14 w-14 rounded-2xl items-center justify-center mx-auto"
            style={{ backgroundColor: `${PALETTE_FOREST}12` }}
          >
            <AlertCircle className="h-7 w-7" style={{ color: PALETTE_FOREST }} />
          </div>
          <h2 className="text-lg font-semibold text-foreground">
            Service request not found
          </h2>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto">
            {error?.message ?? 'We could not load this service request. It may have been removed or you may not have access.'}
          </p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors"
              style={{ backgroundColor: `${PALETTE_FOREST}12`, color: PALETTE_FOREST }}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Retry
            </button>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 text-white transition-colors"
              style={{ backgroundColor: PALETTE_FOREST }}
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const ws = request.customer_workflow_state ?? '';
  const serviceMeta = getServiceMeta(request.service_type);
  // Service Closure: while a closure request is under review (ON_HOLD) or the
  // service is closed (CANCELLED) the backend rejects every customer mutation
  // with 409, so the phase screens render read-only instead of erroring.
  const closureLocked = isServiceLocked(request);

  // -- Phase routing ----------------------------------------------------------

  return (
    <div className="min-h-screen" style={{ backgroundColor: PALETTE_CREAM }}>
      <PageHeader serviceTitle={serviceMeta.title} requestId={id} />

      {/* Stepper -- always visible so the customer knows where they are */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <WorkflowStepper workflowState={request.customer_workflow_state} />
      </div>

      {/* Service Closure — request / pending / declined / closed. Stage-agnostic. */}
      <ServiceClosurePanel
        request={request}
        serviceTitle={serviceMeta.title}
        className="max-w-7xl mx-auto px-4 sm:px-6 pt-4"
      />

      {/* Advance the admin asked for — due or paid. Stage-agnostic; renders
          nothing when no advance is set. Paying is disabled while on hold / closed. */}
      <AdvancePaymentCard
        request={request}
        readOnly={closureLocked}
        className="max-w-7xl mx-auto px-4 sm:px-6 pt-4"
      />

      {/* Phase-specific content. While the service is on hold / closed the
          closure panel above is the complete state screen, so the processing
          copy ("we're analysing your information") is not shown underneath it. */}
      {PROCESSING_STATES.has(ws) && !closureLocked && (
        <ProcessingScreen
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
      )}

      {UPLOAD_STATES.has(ws) && (
        <DocumentUpload request={request} onSuccess={handleRefresh} readOnly={closureLocked} />
      )}

      {WORKSPACE_STATES.has(ws) && (
        <ProviderWorkspace request={request} onRefresh={handleRefresh} readOnly={closureLocked} />
      )}

      {/* Bills issued before the workspace stage (e.g. an advance receipt). The
          workspace shows its own Bills block next to the fee estimate. Renders
          nothing when this request has no bills. */}
      {!WORKSPACE_STATES.has(ws) && (
        <ServiceRequestBills
          serviceRequestId={id}
          className="max-w-xl"
          containerClassName="max-w-7xl mx-auto px-4 sm:px-6 pb-6"
        />
      )}

      {/* Fallback -- unknown state */}
      {!PROCESSING_STATES.has(ws) && !UPLOAD_STATES.has(ws) && !WORKSPACE_STATES.has(ws) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 text-center">
          <p className="text-sm text-muted-foreground">
            Waiting for the service to initialise...
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors disabled:opacity-60"
            style={{ backgroundColor: `${PALETTE_FOREST}12`, color: PALETTE_FOREST }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      )}

      {/* Blocking payment gate — surfaces on ANY stage the moment the admin
          raises `payment_required`, not just once the customer reaches the
          workspace. Renders nothing when no payment is due or once captured.
          Suppressed while the service is on hold / closed — checkout returns
          409 there and closure is only possible before payment anyway. */}
      {!closureLocked && <PaymentGateOverlay request={request} />}
    </div>
  );
}
